import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AppIcon,
  BadgeCircle,
  DIMENSION_SCORE_COLOR_TOKENS,
  DimensionScoreLevelName,
  DimensionScorePill,
  TrackAssessmentCategorySlug,
} from '@backrs_ui/common-ui';

import { StandardsByTrackModal } from '@backrs_ui/common-ui/src/molecules/AssessMyTrack/StandardsByTrackModal';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';

import { Box, IconButton, Typography, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './AssessmentBreakdown.agentContext';
import type {
  AssessmentBreakdownData,
  CategoryScoresData,
  DimensionItem,
} from '../assessMyTrack.types';
import { getGradeLabel, getGradeScorecardTitle } from '../assessMyTrack.utils';
import { UnlockStandards, useUnlockStandards } from '../hooks/useUnlockStandards';

const ASSESS_MY_TRACK_STRINGS = appStrings.modules.assessMyTrack;
const SCORECARD_STRINGS = ASSESS_MY_TRACK_STRINGS;

const SCORE_DISPLAY_LABELS: Record<DimensionScoreLevelName, string> = {
  unmet: SCORECARD_STRINGS.scorecardLevels.notYet,
  minimum: SCORECARD_STRINGS.scorecardLevels.proficient,
  advanced: SCORECARD_STRINGS.scorecardLevels.advanced,
};

type AssessmentBreakdownProps = Readonly<{
  breakdown: AssessmentBreakdownData;
  gradeName: string;
  tenure?: 'High School' | 'Post High School';
  academicGradeId?: number;
  onDimensionClick?: (categorySlug: TrackAssessmentCategorySlug, dimension: DimensionItem) => void;
  isReadOnly?: boolean;
}>;

export const AssessmentBreakdown = ({
  breakdown,
  gradeName,
  tenure,
  academicGradeId,
  onDimensionClick,
  isReadOnly = false,
}: AssessmentBreakdownProps) => {
  useAgentContext(breakdown, SCORE_DISPLAY_LABELS, isReadOnly);

  const unlockStandards = useUnlockStandards(tenure, academicGradeId, getGradeLabel(gradeName));

  // kept after closing so the modal holds its content through the exit transition
  const [shownStandards, setShownStandards] = useState<UnlockStandards | null>(null);
  const [isStandardsOpen, setIsStandardsOpen] = useState(false);

  const showStandards = (standards: UnlockStandards) => {
    setShownStandards(standards);
    setIsStandardsOpen(true);
  };

  const categories = Object.values(breakdown).filter(
    (c): c is CategoryScoresData => !!c && c.dimensions.length > 0,
  );
  const maxRows = Math.max(...categories.map((c) => c.dimensions.length));
  const totalDimensions = categories.reduce((sum, c) => sum + c.dimensions.length, 0);

  const getScorePillLabel = (dimension: DimensionItem): string =>
    dimension.description ?? SCORE_DISPLAY_LABELS[dimension.score];

  const renderDimensionCell = (
    category: CategoryScoresData,
    dimension: DimensionItem,
    isLastRow: boolean,
  ) => (
    <Box
      key={`${category.categorySlug}-${dimension.name}`}
      id={`assess-track-dimension-${category.categorySlug}-${dimension.name.toLowerCase().replace(/\s+/g, '-')}`}
      sx={cellSx(isLastRow, !!onDimensionClick, isReadOnly)}
      onClick={
        onDimensionClick ? () => onDimensionClick(category.categorySlug, dimension) : undefined
      }
    >
      <Box sx={cellContentSx}>
        <Box sx={dimensionNameRowSx}>
          <Typography variant="text-sm-semibold" sx={dimensionNameSx}>
            {dimension.name}
          </Typography>
          <Box sx={trailingIconSx}>
            {dimension.editType === 'dimension-toggle' &&
              dimension.lubavScore !== dimension.score && (
                <Box className="badge-icon" sx={badgeIconSx}>
                  <BadgeCircle size="md" crossedOut />
                </Box>
              )}
            {!isReadOnly && (
              <Box className="edit-icon-desktop" sx={editIconDesktopSx}>
                <AppIcon iconName="Edit02" diameter="14px" color="Gray/400" />
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={pillRowSx}>
          <DimensionScorePill
            score={dimension.score}
            label={getScorePillLabel(dimension)}
            sx={scorePillSx}
          />
          {!isReadOnly && (
            <Box sx={editIconMobileSx}>
              <AppIcon iconName="Edit02" diameter="14px" color="Gray/400" />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderCategoryHeader = (category: CategoryScoresData) => {
    const standards = unlockStandards[category.categorySlug];

    return (
      <Box key={category.categorySlug} sx={headerCellSx}>
        <Typography variant="text-md-semibold">{category.categoryName}</Typography>
        {standards && (
          <IconButton
            id={`ayot-${category.categorySlug}-standards-info-btn`}
            size="small"
            onClick={() => showStandards(standards)}
            sx={infoButtonSx}
          >
            <AppIcon iconName="InfoCircle" diameter="14px" color="Gray/400" />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderCategoryButtons = (category: CategoryScoresData) => (
    <Box key={`${category.categorySlug}-buttons`} sx={buttonsCellSx}>
      <LubavButton
        id={`ayot-${category.categorySlug}-strengths-btn`}
        label="Strengths"
        promptButtonId={`ayot_${category.categorySlug.replace(/-/g, '_')}_strengths`}
        buttonSize="sm"
      />
      <LubavButton
        id={`ayot-${category.categorySlug}-opportunities-btn`}
        label="Opportunities"
        promptButtonId={`ayot_${category.categorySlug.replace(/-/g, '_')}_opportunities`}
        buttonSize="sm"
      />
    </Box>
  );

  return (
    <Box sx={containerSx}>
      {/* Header: title + subtitle + legend */}
      <Box sx={headerSx}>
        <Box sx={headerTextSx}>
          <Typography sx={titleSx}>{getGradeScorecardTitle(gradeName)}</Typography>
          <Typography sx={subtitleSx}>
            {SCORECARD_STRINGS.scorecardSubtitle(totalDimensions)}
            {isReadOnly ? (
              SCORECARD_STRINGS.scorecardSubtitleLinkText
            ) : (
              <Link
                id="assess-track-four-unlocks-link"
                to={APP_ROUTE.LIBRARY}
                style={{ textDecoration: 'none' }}
              >
                <Typography component="span" sx={fourUnlocksLinkSx}>
                  {SCORECARD_STRINGS.scorecardSubtitleLinkText}
                </Typography>
              </Link>
            )}
            {isReadOnly
              ? SCORECARD_STRINGS.scorecardSubtitleSuffixReadOnly
              : SCORECARD_STRINGS.scorecardSubtitleSuffix}
          </Typography>
        </Box>
        <Box sx={legendSx}>
          <Box sx={legendItemSx}>
            <Box sx={legendCircleSx('advanced')} />
            <Typography variant="text-xs-medium">
              {SCORECARD_STRINGS.scorecardLevels.advanced}
            </Typography>
          </Box>
          <Box sx={legendItemSx}>
            <Box sx={legendCircleSx('minimum')} />
            <Typography variant="text-xs-medium">
              {SCORECARD_STRINGS.scorecardLevels.proficient}
            </Typography>
          </Box>
          <Box sx={legendItemSx}>
            <Box sx={legendCircleSx('unmet')} />
            <Typography variant="text-xs-medium">
              {SCORECARD_STRINGS.scorecardLevels.notYet}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Mobile: vertical stack per category */}
      <Box sx={mobileStackSx}>
        {categories.map((category) => (
          <Box key={category.categorySlug}>
            {renderCategoryHeader(category)}
            {category.dimensions.map((dimension, idx) =>
              renderDimensionCell(category, dimension, idx === category.dimensions.length - 1),
            )}
            {renderCategoryButtons(category)}
          </Box>
        ))}
      </Box>

      {/* Desktop: 4-column grid */}
      <Box sx={gridSx}>
        {categories.map((category) => renderCategoryHeader(category))}

        {Array.from({ length: maxRows }, (_, rowIdx) =>
          categories.map((category) => {
            const dimension = category.dimensions[rowIdx];
            const isLastRow = rowIdx === maxRows - 1;

            if (!dimension) {
              return (
                <Box
                  key={`${category.categorySlug}-empty-${rowIdx}`}
                  sx={cellSx(isLastRow, false, isReadOnly)}
                />
              );
            }

            return renderDimensionCell(category, dimension, isLastRow);
          }),
        )}

        {categories.map((category) => renderCategoryButtons(category))}
      </Box>

      {shownStandards && (
        <StandardsByTrackModal
          open={isStandardsOpen}
          onClose={() => setIsStandardsOpen(false)}
          category={shownStandards.category}
          variant={shownStandards.variant}
        />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '900px',
  backgroundColor: backrs.colors['Colors/Base/White'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-4xl'],
  flexWrap: 'wrap',
  padding: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    padding: backrs.spacing['spacing-xl'],
  },
  '@container (min-width: 650px)': {
    padding: backrs.spacing['spacing-2xl'],
  },
});

const headerTextSx: SxProps<Theme> = () => ({
  flex: 1,
  minWidth: 200,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  textAlign: 'left',
  mb: backrs.spacing['spacing-xs'],
  typography: 'text-lg-semibold',
  '@container (min-width: 650px)': {
    typography: 'display-xs-semibold',
  },
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
  typography: 'text-xs-regular',
  '@container (min-width: 650px)': {
    typography: 'text-md-regular',
  },
});

const fourUnlocksLinkSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/700'],
  typography: 'text-xs-bold',
  '@container (min-width: 650px)': {
    typography: 'text-md-bold',
  },
});

const legendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'none',
  '@container (min-width: 650px)': {
    display: 'flex',
  },
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  pt: backrs.spacing['spacing-xs'],
  mx: backrs.spacing['spacing-2xl'],
});

const legendItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  color: backrs.colors['Gray/600'],
});

const legendCircleSx =
  (score: DimensionScoreLevelName): SxProps<Theme> =>
  ({ backrs }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    boxSizing: 'content-box',
    backgroundColor:
      score === 'unmet' ? 'transparent' : backrs.colors[DIMENSION_SCORE_COLOR_TOKENS[score]],
    border: `1.5px solid ${backrs.colors['Colors/Base/Black']}`,
  });

const infoButtonSx: SxProps<Theme> = {
  padding: '2px',
};

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'none',
  '@container (min-width: 650px)': {
    display: 'grid',
  },
  gridTemplateColumns: 'repeat(4, 1fr)',
  '& > :nth-of-type(n+5):not(:nth-of-type(4n+1))': {
    borderLeft: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  },
});

const mobileStackSx: SxProps<Theme> = {
  display: 'flex',
  '@container (min-width: 650px)': {
    display: 'none',
  },
  flexDirection: 'column',
};

const headerCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  px: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  pt: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    px: backrs.spacing['spacing-xl'],
  },
  pb: backrs.spacing['spacing-md'],
  borderBottom: `1px solid ${backrs.colors['Gray/500']}`,
  color: backrs.colors['Gray/500'],
});

const cellSx =
  (isLastRow: boolean, isClickable: boolean, isReadOnly: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    px: backrs.spacing['spacing-lg'],
    '@container (min-width: 400px)': {
      px: backrs.spacing['spacing-xl'],
    },
    py: backrs.spacing['spacing-lg'],
    ...(isClickable && {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: backrs.colors['Brand/50'],
        ...(!isReadOnly && {
          '& .badge-icon': {
            opacity: 0,
          },
          '& .edit-icon-desktop': {
            opacity: 1,
          },
        }),
      },
    }),
    ...(!isLastRow && {
      borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
    }),
    ...(isLastRow && {
      pb: backrs.spacing['spacing-xl'],
    }),
  });

const cellContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flex: 1,
  '@container (min-width: 650px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

const dimensionNameRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
  width: '100%',
});

const dimensionNameSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  color: backrs.colors['Gray/900'],
  '@container (min-width: 650px)': {
    minHeight: '2.5em',
  },
});

const trailingIconSx: SxProps<Theme> = () => ({
  position: 'relative',
  width: 16,
  height: 16,
  flexShrink: 0,
});

const badgeIconSx: SxProps<Theme> = () => ({
  position: 'absolute',
  inset: 0,
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 0.15s',
  '@container (min-width: 650px)': {
    display: 'flex',
  },
});

const editIconDesktopSx: SxProps<Theme> = () => ({
  position: 'absolute',
  inset: 0,
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity 0.15s',
  opacity: 0,
  '@container (min-width: 650px)': {
    display: 'flex',
  },
});

const pillRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const editIconMobileSx: SxProps<Theme> = () => ({
  display: 'flex',
  alignItems: 'center',
  '@container (min-width: 650px)': {
    display: 'none',
  },
});

const buttonsCellSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xs'],
  px: backrs.spacing['spacing-lg'],
  '@container (min-width: 400px)': {
    px: backrs.spacing['spacing-xl'],
  },
  '@container (min-width: 650px)': {
    minHeight: '100px',
  },
  py: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const scorePillSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
};
